"use client";

import { useLocale } from "@i18n/react";
import { useMutation, useQueryClient } from "@tanstack/react-query";

import type { PaymentResult } from "../../../lib/types/schema/subscription/cartSchema";
import { cartSchema } from "../../../lib/types/schema/subscription/cartSchema";
import {
  cancelSubscriptionSchema,
  refundPaymentSchema,
  subscriptionDetailSchema,
  subscriptionSchema,
} from "../../../lib/types/schema/subscription/subscriptionSchema";
import { removeNullKeys } from "../../../lib/utils/removeNullKeys";
import type { paths } from "../../../request/generated/schema";
import { useRequestMutation } from "../../../request/hooks/useRequestMutation";
import { useRequestQuery } from "../../../request/hooks/useRequestQuery";
import { openApiRequest } from "../../../request/openApiRequest";
import { keyMe } from "../me/keyMe";
import { keyPaymentMethod } from "../me/payments/keyPaymentMethod";
import {
  keyCancelFlow,
  keySubscription,
  keySubscriptions,
} from "./keySubscriptions";

type SubscriptionPurchase = PaymentResult & {
  priceId: string;
  promoCode?: string;
};

type SubscriptionPaymentInfo = {
  token: string;
};

type SubscriptionPurchaseV2 =
  paths["/subscription/stripe/async"]["post"]["requestBody"]["content"]["application/json"];

type SubscriptionChangePlan = {
  price_id: string;
  subscription_id: number;
};

type SubscriptionAction = {
  subscription_id: number;
};

type UseRequestSubscriptionProps = {
  isQuery?: boolean;
};

export const useRequestSubscription = ({
  isQuery = true,
}: UseRequestSubscriptionProps = {}) => {
  const locale = useLocale();
  const queryClient = useQueryClient();

  const handleMutationSuccess = async () => {
    await queryClient.invalidateQueries({
      queryKey: [keySubscription(), locale],
    });
    await queryClient.invalidateQueries({
      queryKey: [keySubscriptions(), locale],
    });
    await queryClient.invalidateQueries({
      queryKey: [keyCancelFlow(), locale],
    });
    await queryClient.invalidateQueries({
      queryKey: [keyPaymentMethod(), locale],
    });
  };

  return {
    query: useRequestQuery({
      enabled: isQuery,
      queryFnRequestProps: {
        schema: subscriptionDetailSchema,
        urlBackendPath: "/subscription",
      },
      queryKey: keySubscription(),
      retryOnMount: false,
      retry: 0,
    }),
    mutationSubscribe: useRequestMutation({
      mutationFnRequestProps: (data: SubscriptionPurchase) => ({
        body: JSON.stringify({
          payment_method: data.paymentMethod,
          payment_platform: data.paymentPlatform,
          price_id: data.priceId,
          promo_code: data.promoCode?.length ? data.promoCode : undefined,
          email: data.email,
          address: data.address ? removeNullKeys(data.address) : undefined,
        }),
        method: "POST",
        schema: subscriptionSchema,
        urlBackendPath: "/subscription",
      }),
      onSuccess: async () => {
        await handleMutationSuccess();
        await queryClient.invalidateQueries({ queryKey: keyMe() });
      },
    }),
    // No cache invalidation here on purpose: this creates a `default_incomplete`
    // subscription with a short-lived end_date BEFORE the payment is confirmed.
    // Invalidating now would surface it app-wide (a declined user would look
    // subscribed); the checkout flow refetches after the confirm settles.
    mutationSubscribeV2: useMutation({
      mutationFn: (data: SubscriptionPurchaseV2) =>
        openApiRequest({
          method: "POST",
          urlBackendPath: "/subscription/stripe/async",
          body: {
            price_id: data.price_id,
            promo_code: data.promo_code?.length ? data.promo_code : undefined,
            email: data.email || undefined,
            address: data.address ? removeNullKeys(data.address) : undefined,
          },
        }),
    }),
    mutationPaymentInfo: useRequestMutation({
      mutationFnRequestProps: (data: SubscriptionPaymentInfo) => ({
        body: JSON.stringify(data),
        method: "PUT",
        urlBackendPath: "/subscription/payment",
      }),
      onSuccess: handleMutationSuccess,
    }),
    mutationChangePlan: useRequestMutation<
      typeof subscriptionSchema,
      SubscriptionChangePlan
    >({
      mutationFnRequestProps: (data: SubscriptionChangePlan) => ({
        body: JSON.stringify(data),
        method: "PUT",
        schema: subscriptionSchema,
        urlBackendPath: "/subscription",
      }),
      onSuccess: async () => {
        await handleMutationSuccess();
        await queryClient.invalidateQueries({ queryKey: keyMe() });
      },
    }),
    mutationUpgradeMobile: useRequestMutation({
      mutationFnRequestProps: (data: SubscriptionPurchase) => ({
        body: JSON.stringify({
          payment_method: data.paymentMethod,
          payment_platform: data.paymentPlatform,
          price_id: data.priceId,
          promo_code: data.promoCode?.length ? data.promoCode : undefined,
          email: data.email,
          address: data.address ? removeNullKeys(data.address) : undefined,
        }),
        method: "PUT",
        schema: subscriptionSchema,
        urlBackendPath: "/subscription/iap-upgrade",
      }),
      onSuccess: handleMutationSuccess,
    }),
    mutationCancel: useRequestMutation({
      mutationFnRequestProps: (data: SubscriptionAction) => ({
        body: JSON.stringify(data),
        method: "PUT",
        schema: cancelSubscriptionSchema,
        urlBackendPath: "/subscription/cancel",
      }),
      onSuccess: async () => {
        await handleMutationSuccess();
        await queryClient.invalidateQueries({ queryKey: keyMe() });
      },
    }),
    mutationRefund: useRequestMutation({
      mutationFnRequestProps: (data: SubscriptionAction) => ({
        body: JSON.stringify(data),
        method: "PUT",
        schema: refundPaymentSchema,
        urlBackendPath: "/subscription/refund",
      }),
      onSuccess: handleMutationSuccess,
    }),
    mutationRestart: useRequestMutation({
      mutationFnRequestProps: (data: SubscriptionAction) => ({
        body: JSON.stringify(data),
        method: "PUT",
        schema: subscriptionSchema,
        urlBackendPath: "/subscription/restart",
      }),
      onSuccess: handleMutationSuccess,
    }),
    mutationApplyPromo: useRequestMutation({
      mutationFnRequestProps: (data: { promoCode: string }) => ({
        body: JSON.stringify({ promo_code: data.promoCode }),
        method: "PUT",
        schema: cartSchema,
        urlBackendPath: "/subscription/promo",
      }),
      onSuccess: handleMutationSuccess,
    }),
    mutationExtend: useRequestMutation({
      mutationFnRequestProps: (data: {
        days: number;
        subscription_id: number;
      }) => ({
        method: "POST",
        body: JSON.stringify(data),
        schema: subscriptionSchema,
        urlBackendPath: "/subscription/extend-trial",
      }),
      onSuccess: async () => {
        await handleMutationSuccess();
        await queryClient.invalidateQueries({ queryKey: keyMe() });
      },
    }),
  };
};
